import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

interface TrainingMethodsTableProps {
  sportName: string;
}

const TRAINING_METHOD_ROWS = [
  {
    method: '1:1 coaching',
    bestFor: 'Rapid technical correction',
    drawback: 'Expensive; low throughput',
    coachTime: '60-100%',
    whenToUse: 'New beginners first 4-6 sessions; elite players.',
  },
  {
    method: 'Coach-led group',
    bestFor: 'Controlled progress; safety',
    drawback: 'Less individual correction',
    coachTime: '40-60%',
    whenToUse: 'Small groups (<=4 per coach).',
  },
  {
    method: 'Coach + role rotation',
    bestFor: 'Scale + leadership + tactical IQ',
    drawback: 'Needs role training; risk of poor peer feedback',
    coachTime: '20-50% (varies by level)',
    whenToUse: 'Clubs wanting efficiency + quality.',
  },
  {
    method: 'Self / home practice',
    bestFor: 'Repetition, conditioning',
    drawback: 'Risk of ingraining errors',
    coachTime: '0-10%',
    whenToUse: 'Supplementary drills, S&C.',
  },
  {
    method: 'Sparring / tournaments',
    bestFor: 'Match adaptation',
    drawback: 'Variable opponent quality; can mislead learning',
    coachTime: 'Coach as observer/analyst',
    whenToUse: 'Match readiness after foundation.',
  },
] as const;

export function TrainingMethodsTable({ sportName }: TrainingMethodsTableProps) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Different training methods can be chosen based on convenience, cost, access to coaching, and player goals. The table below uses badminton practice as a reference and may vary for {sportName}.
      </Typography>
      <TableContainer sx={{ border: '1px solid #d7e3f4', borderRadius: 1, bgcolor: '#fff' }}>
        <Table size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f4f8ff' }}>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Method</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Best for</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Main drawback</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Coach time per player</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>When to use</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {TRAINING_METHOD_ROWS.map((row) => (
              <TableRow key={row.method}>
                <TableCell sx={{ fontWeight: 700 }}>{row.method}</TableCell>
                <TableCell>{row.bestFor}</TableCell>
                <TableCell>{row.drawback}</TableCell>
                <TableCell>{row.coachTime}</TableCell>
                <TableCell>{row.whenToUse}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
